import { Component, Inject, OnInit } from '@angular/core';
import { MatIconRegistry } from '@angular/material/icon';
import { DomSanitizer } from '@angular/platform-browser';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { FuseConfigService } from '@theme/services/config.service';
import { DeviceDetectorService } from 'ngx-device-detector';
import { catchError, first, tap } from 'rxjs/operators';
import { theme_config } from './config/theme_config';
import { TemplateCustomConfig } from './models/TemplateCustomConfig';
import { ManageUsersAccountService } from './services/manage-users-account.service';
import { of } from 'rxjs';
import { AppConfig, ICARE_APP_CONFIG } from './services/app-config.service';
import * as braze from '@braze/web-sdk';
import { VendorsService } from './services/vendors.service';

declare let gtag: Function;

@Component({
    selector: 'app-root',
    templateUrl: './app.component.html',
    styleUrls: ['./app.component.scss'],
    standalone: false
})
export class AppComponent implements OnInit {

  title = 'intelycare';
  uiconfig = theme_config;
  _RoutesWithLeftSideBar = [];
  _NavbarRoutes = ['/directory', '/settings', '/monthly', '/week', '/request-intelypro', '/dashboard', '/timecards', '/home'];
  private isGAUserSet: boolean;
  enableApp: boolean;
  web: boolean;

  constructor (
    private _router: Router,
    private _route: ActivatedRoute,
    private _domSan: DomSanitizer,
    private _matIconRegistry: MatIconRegistry,
    private _fuseConfigService: FuseConfigService,
    private _deviceService: DeviceDetectorService,
    private _userService: ManageUsersAccountService,
    private _vendorsService: VendorsService,
    @Inject(ICARE_APP_CONFIG) private _config: AppConfig
  ) {

    this._route.queryParams.pipe(
      tap((params) => {
        if (this._config.ALLOW_ANALYTICS) {
          gtag('js', new Date());
          gtag('config', 'UA-55670953-1', {
            'campaign': {
              'keyword': params['utm_term'],
              'name': params['utm_campaign'],
              'medium': params['utm_medium'],
              'source': params['utm_source'],
              'content': params['utm_content']
            },
            'custom_map': {
              'metric1': 'metric_facility_id',
              'dimension2': 'facility_id'
            }
          });
        }
      })
    ).subscribe();

    _router.events.subscribe(event => {
      const user = this._userService.currentUserValue;
      if (event instanceof NavigationEnd) {
        const customConfig: TemplateCustomConfig = { navbar: '', sidebar: '' };

        if (!this._RoutesWithLeftSideBar.includes(_router.url)) {
          this.uiconfig.layout.sidepanel.hidden = true;
        } else {
          customConfig.sidebar = 'DefaultSidebar';
          this.uiconfig.layout.sidepanel.hidden = false;
        }

        if (!this._NavbarRoutes.some(e => _router.url.includes(e))) {
          this.uiconfig.layout.toolbar.hidden = true;
        } else {
          customConfig.navbar = 'DefaultNavbar';
          this.uiconfig.layout.toolbar.hidden = false;
        }

        this.uiconfig.layout.sidepanel.position = 'left';
        this._fuseConfigService.setConfig(this.uiconfig);
        this._fuseConfigService.setCustomConfig = customConfig;

        if (this._config.ALLOW_ANALYTICS) {
          gtag('config', 'GA_MEASUREMENT_ID', {
            'campaign': {
              'keyword': event['snapshot']?.queryParams['utm_term'],
              'name': event['snapshot']?.queryParams['utm_campaign'],
              'medium': event['snapshot']?.queryParams['utm_medium'],
              'source': event['snapshot']?.queryParams['utm_source'],
              'content': event['snapshot']?.queryParams['utm_content']
            },
            'page_path': event.urlAfterRedirects,
            'facility_id': user ? user.activeusertype ? user.activeusertype.clientid : '' : '',
            'metric_facility_id': user ? user.activeusertype ? user.activeusertype.clientid : '' : ''
          });
        }

        this._setUserIdGoogleAnalytics();
      }

      braze.requestContentCardsRefresh();
    });

    this.web = this._deviceService.isDesktop();
    this.enableApp = true;
  }

  ngOnInit(): void {
    this._matIconRegistry.addSvgIcon(
      `intelypro`,
      this._domSan.bypassSecurityTrustResourceUrl(`assets/images/branding/intelycare-logo-icon-round-border-med.svg`)
    );
    this._matIconRegistry.addSvgIcon(
      `staff-icon`,
      this._domSan.bypassSecurityTrustResourceUrl(`assets/images/staff-icon.svg`)
    );
    this._matIconRegistry.addSvgIcon(
      `user-edit`,
      this._domSan.bypassSecurityTrustResourceUrl(`assets/images/user-edit.png`)
    );
    this._matIconRegistry.addSvgIcon(
      `user-edit-confirm`,
      this._domSan.bypassSecurityTrustResourceUrl(`assets/images/user-edit-confirm.png`)
    );
    this._matIconRegistry.addSvgIcon(
      `user-remove`,
      this._domSan.bypassSecurityTrustResourceUrl(`assets/images/trash.png`)
    );

    const user = this._userService.currentUserValue;
    const zendeskToken = this.getZendeskToken();
    if (user && zendeskToken && !this.isZendeskTokenExpired(zendeskToken)) {
      // Zendesk token exists and is valid. Skipping authentication
    } else {
      this._userService.initializeBraze();
      this._vendorsService.authenticateZendeskChatSession(user.firstname, user.lastname, user.email).pipe(
        first(),
        tap((response) => {
          if (!response.data) {
            console.error('Error authenticating Zendesk chat session');
          }
        }),
        catchError(() => {
          console.error('Failed to authenticate Zendesk chat session');
          return of(null);
        })
      ).subscribe();
    }
  }

  private getZendeskToken(): { token: string; expiry: number } | null {
    const tokenString = localStorage.getItem('ZendeskAuthToken');
    if (tokenString) {
      return JSON.parse(tokenString);
    }
    return null;
  }

  private isZendeskTokenExpired(tokenData: { token: string; expiry: number }): boolean {
    const currentTime = Math.floor(Date.now() / 1000); // Current time in seconds
    return currentTime >= tokenData.expiry;
  }
  
  // Helper method to save the Zendesk token and expiration time
  private saveZendeskToken(token: string, expiry: number): void {
    const tokenData = { token, expiry };
    localStorage.setItem('ZendeskAuthToken', JSON.stringify(tokenData));
  }

  private _setUserIdGoogleAnalytics() {
    const user = this._userService.currentUserValue;
    if (this._config.ALLOW_ANALYTICS && user && user.active && !this.isGAUserSet) {
      gtag('set', { 'user_id': user.activeusertype.clientid });
      this.isGAUserSet = true;
    }
  }

}
